// Client-side image downscaling. Big phone photos (multi-MB each) over LTE
// blow past the gateway's request body limit and cause 520s before the
// request ever reaches the backend. Shrinking in the browser first keeps every
// upload tiny (~200-400KB) and fixes that at the source.

export const MAX_UPLOAD_BYTES = 8 * 1024 * 1024;

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

export async function compressImage(file, opts = {}) {
  const { maxEdge = 2000, quality = 0.82 } = opts;
  if (!file || !/^image\//i.test(file.type)) return file;
  let url;
  try {
    url = URL.createObjectURL(file);
    const img = await loadImage(url);
    const longest = Math.max(img.naturalWidth, img.naturalHeight);
    const scale = Math.min(1, maxEdge / longest);
    if (scale === 1 && file.size <= 900 * 1024) return file; // already small
    const w = Math.max(1, Math.round(img.naturalWidth * scale));
    const h = Math.max(1, Math.round(img.naturalHeight * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, w, h);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
    if (!blob || blob.size >= file.size) return file;
    const name = (file.name || "image").replace(/\.[^.]+$/, "") + ".jpg";
    return new File([blob], name, { type: "image/jpeg", lastModified: Date.now() });
  } catch {
    return file; // decode failed (e.g. HEIC) — send original and let backend validate
  } finally {
    if (url) URL.revokeObjectURL(url);
  }
}

export async function compressMany(files, opts) {
  const out = [];
  for (const f of files || []) out.push(await compressImage(f, opts));
  return out;
}
